/**
 * Segment replace-mode import (partner newsletter roster sync).
 * Client for the staged run pipeline: begin → stage → preview → commit/abort.
 * Implements docs/DESIGN_SEGMENT_REPLACE_IMPORT.md rev 12 §5, including the
 * per-RPC retry semantics: stage = blind retry safe (server chunk-hash
 * idempotency); begin = same begin_key adopts; preview = re-invoke safe;
 * commit = identical-args replay ONLY (never the generic retry wrapper —
 * a lost response after a successful commit must reconcile by replay, not
 * report failure on "already committed").
 */

import { supabase } from "./supabase";
import {
  isSmsNumberBusy,
  retryOnSmsNumberBusy,
  SMS_NUMBER_BUSY_MESSAGE,
} from "../utils/smsNumberBusy";

export type ImportRunMode = "append" | "replace";
export type NewContactClassification = "prospect" | "unclassified";

export interface BeginRunResult {
  run_id: string;
  status: string;
  adopted: boolean;
}

export interface StageResult {
  staged_total: number;
  invalid_chunk: number;
  retry_noop: boolean;
}

export interface PreviewResult {
  staged: number;
  invalid_total: number;
  add: number;
  keep: number;
  remove: number;
  remove_no_email: number;
  new_contacts: number;
  current_members: number;
  remove_sample: Array<{ email: string | null; name: string }>;
  mode: ImportRunMode;
  remove_set_hash: string;
  staged_set_hash: string;
}

export interface CommitResult {
  mode: ImportRunMode;
  added_members: number;
  new_contacts: number;
  planned_remove: number;
  actual_removed: number;
  members_before: number;
  members_after: number;
}

export interface ImportRunSummary {
  id: string;
  list_id: string | null;
  list_name: string;
  mode: ImportRunMode;
  status: string;
  filename: string | null;
  preview: Record<string, unknown> | null;
  result: CommitResult | null;
  created_by: string;
  created_at: string;
  committed_at: string | null;
}

/** PostgREST codes meaning the statement never reached/executed on the
 *  database (connection or pool acquisition failure) — safe to retry. */
const PGRST_CONNECTION_CODES = new Set([
  "PGRST000",
  "PGRST001",
  "PGRST002",
  "PGRST003",
]);

// Mirrors the server CHUNK_SIZE constant.
export const REPLACE_IMPORT_CHUNK_SIZE = 250;
// Mirrors the server MAX_STAGED_ROWS constant (migration 005: 8s-budget cap).
export const REPLACE_IMPORT_MAX_ROWS = 25000;
// prepare batch sized for 2x headroom under the 8s ceiling (2000 measured 5.1s).
// B1 (two-way SMS build, owner 2026-10-01): a few hundred contacts per transaction keeps each call's
// lock-table use small (every created contact holds number gates + e-mail locks until commit). The server
// caps p_max at 500.
export const PREPARE_CONTACTS_BATCH = 250;
// 25,000 staged rows max (server C_MAX_STAGED_ROWS) / 250 per call = 100 calls, plus headroom.
export const PREPARE_MAX_CALLS = 120;

/** Mirrors the server's commit workload budget (migration 007): measured rates
 *  adds ~7,400 rows/s, removals+audit ~11,000 rows/s, 4.0s budget. The server
 *  remains authoritative — this lets PREVIEW refuse before `prepare`
 *  permanently creates contacts (round-3 M). */
export function exceedsCommitBudget(
  stagedRows: number,
  removals: number,
): boolean {
  return stagedRows / 7400 + removals / 11000 > 4.0;
}

/** Round-2 M: classify by the ERROR OBJECT, not message prefix alone. Any
 *  error carrying a PostgreSQL/PostgREST code (SQLSTATE like 23505/57014/42501
 *  or PGRSTxxx) is a DEFINITIVE server response — the statement reached the
 *  server and was refused/aborted. Only code-less failures (fetch/network) are
 *  "transport" and eligible for retry/replay. */
export function classifyImportErrorObject(err: {
  code?: string | null;
  message?: string | null;
}): ReturnType<typeof classifyImportError> {
  const byMessage = classifyImportError(err.message ?? "");
  if (byMessage !== "transport") {
    return byMessage;
  }
  if (err.code) {
    // Round-3 M: PostgREST connection-family codes mean the statement never
    // executed (no DB connection / pool acquisition failure) — retryable.
    // Everything else carrying a code (SQLSTATE 23505/57014/42501, PGRST1xx
    // request errors) is a definitive server response.
    if (PGRST_CONNECTION_CODES.has(err.code)) {
      return "transport";
    }
    return "server_refusal";
  }
  return "transport";
}

/** Distinct server error classes the wizard maps to specific UX flows.
 *  Code-audit M6: EVERY SEG_IMPORT_* error is a DEFINITIVE server refusal —
 *  "server_refusal" is the catch-all for families without a dedicated UX flow
 *  (STATE, INPUT, NOT_FOUND, INVARIANT, CHUNK_CONFLICT, INTERNAL). Only
 *  "transport" may ever be retried/replayed. */
export function classifyImportError(
  message: string,
):
  | "ack_required"
  | "stale_preview"
  | "contention"
  | "key_mismatch"
  | "sealed"
  | "target"
  | "bounds"
  | "auth"
  | "server_refusal"
  | "transport" {
  if (message.includes("SEG_IMPORT_ACK_REQUIRED")) {
    return "ack_required";
  }
  if (message.includes("SEG_IMPORT_STALE_PREVIEW")) {
    return "stale_preview";
  }
  if (message.includes("SEG_IMPORT_CONTENTION")) {
    return "contention";
  }
  if (message.includes("SEG_IMPORT_KEY_MISMATCH")) {
    return "key_mismatch";
  }
  if (message.includes("SEG_IMPORT_SEALED")) {
    return "sealed";
  }
  if (message.includes("SEG_IMPORT_TARGET")) {
    return "target";
  }
  if (message.includes("SEG_IMPORT_BOUNDS")) {
    return "bounds";
  }
  if (message.includes("SEG_IMPORT_AUTH")) {
    return "auth";
  }
  if (message.includes("SEG_IMPORT_")) {
    return "server_refusal";
  }
  return "transport";
}

export async function beginImportRun(input: {
  listId: string;
  mode: ImportRunMode;
  classification: NewContactClassification;
  filename: string | null;
  beginKey: string; // fresh UUID per wizard entry (crypto.randomUUID())
}): Promise<BeginRunResult> {
  const { data, error } = await supabase.rpc("begin_segment_import_run", {
    p_list_id: input.listId,
    p_mode: input.mode,
    p_new_contact_classification: input.classification,
    p_filename: input.filename,
    p_source: "import",
    p_begin_key: input.beginKey,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as BeginRunResult;
}

export async function stageImportChunk(
  runId: string,
  chunkIndex: number,
  rows: Array<Record<string, string>>,
): Promise<StageResult> {
  // Blind retry is safe here: the server recomputes the sanitized chunk hash
  // and treats an identical re-send as a no-op.
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const { data, error } = await supabase.rpc("stage_segment_import_rows", {
      p_run_id: runId,
      p_chunk_index: chunkIndex,
      p_rows: rows,
    });
    if (!error) {
      return data as StageResult;
    }
    lastError = new Error(error.message);
    // Only transport failures retry; every server refusal surfaces immediately.
    if (classifyImportErrorObject(error) !== "transport") {
      throw lastError;
    }
    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
  }
  throw lastError ?? new Error("stage failed");
}

export async function previewImportRun(runId: string): Promise<PreviewResult> {
  const { data, error } = await supabase.rpc("preview_segment_import_run", {
    p_run_id: runId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as PreviewResult;
}

/**
 * Commit with reconcile-by-replay (rev 12 §5.4): the server is idempotent for
 * identical arguments against a committed run, so on transport ambiguity we
 * simply re-call with the SAME arguments. Bounded attempts; a still-unknown
 * outcome surfaces as "unknown" so the UI shows reconciling state and defers
 * to the durable runs history — never a fabricated failure.
 */
export async function commitImportRun(input: {
  runId: string;
  expectedRemoveCount: number;
  expectedRemoveHash: string;
  acknowledgeLargeRemoval: boolean;
}): Promise<CommitResult> {
  let lastTransportError: Error | null = null;
  let preparedAgain = false;
  for (let attempt = 0; attempt < 4; attempt++) {
    // SMS plan §4.3: commit creates contacts, so it can abort whole on SMS_NUMBER_BUSY (nothing
    // written); the identical call is re-run by the bounded busy retry. An exhausted busy refusal
    // surfaces as a definitive, retry-in-a-moment message (the wizard returns to the preview step).
    const { data, error } = await retryOnSmsNumberBusy(() =>
      supabase.rpc("commit_segment_import_run", {
        p_run_id: input.runId,
        p_expected_remove_count: input.expectedRemoveCount,
        p_expected_remove_hash: input.expectedRemoveHash,
        p_acknowledge_large_removal: input.acknowledgeLargeRemoval,
      }),
    );
    if (!error) {
      return data as CommitResult;
    }
    if (isSmsNumberBusy(error)) {
      throw new Error(SMS_NUMBER_BUSY_MESSAGE);
    }
    // B1: commit refuses (before any write) when more than a small residue of contacts still has to be
    // created; finish the batched prepare phase and commit again with the same arguments.
    if (
      typeof error.message === "string" &&
      error.message.includes("SEG_IMPORT_UNPREPARED") &&
      !preparedAgain
    ) {
      preparedAgain = true;
      await prepareAllImportContacts(input.runId);
      attempt -= 1;
      continue;
    }
    // Server-side refusals are definitive — surface immediately.
    if (classifyImportErrorObject(error) !== "transport") {
      throw new Error(error.message);
    }
    // Transport-ambiguous: replay with identical args after a backoff.
    lastTransportError = new Error(error.message);
    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
  }
  throw new Error(
    `IMPORT_OUTCOME_UNKNOWN: could not confirm the commit outcome (${
      lastTransportError?.message ?? "network"
    }). Check the segment's import history — if the run shows Committed, the import succeeded.`,
  );
}

/** Map raw CSV rows through the wizard's field mappings into the RPC row
 *  shape, preserving FILE ORDER (the server's source_ord + first-row-wins
 *  dedupe depend on it). Invalid rows are sent too — the server is the
 *  validation authority in this pipeline and its chunk hash covers every row. */
export function mapRowsForImport(
  rows: Array<Record<string, string>>,
  mappings: Array<{ csvColumn: string; contactField: string | null }>,
): Array<Record<string, string>> {
  return rows.map((row) => {
    const out: Record<string, string> = {};
    for (const m of mappings) {
      if (!m.contactField) {
        continue;
      }
      const v = row[m.csvColumn];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        out[m.contactField] = String(v);
      }
    }
    return out;
  });
}

/** Stage an entire mapped row set as sequential 250-row chunks. */
export async function stageAllChunks(
  runId: string,
  mappedRows: Array<Record<string, string>>,
  onProgress?: (staged: number, total: number) => void,
): Promise<{ stagedTotal: number; invalidTotal: number }> {
  let stagedTotal = 0;
  let invalidTotal = 0;
  for (let i = 0; i < mappedRows.length; i += REPLACE_IMPORT_CHUNK_SIZE) {
    const chunk = mappedRows.slice(i, i + REPLACE_IMPORT_CHUNK_SIZE);
    const res = await stageImportChunk(
      runId,
      Math.floor(i / REPLACE_IMPORT_CHUNK_SIZE),
      chunk,
    );
    stagedTotal = res.staged_total;
    invalidTotal += res.invalid_chunk;
    onProgress?.(
      Math.min(i + chunk.length, mappedRows.length),
      mappedRows.length,
    );
  }
  return { stagedTotal, invalidTotal };
}

/** Client-side mirror of the server's inclusive large-removal rail, used only
 *  to show the typed-ack input proactively; the server re-evaluates on its
 *  own snapshot and remains authoritative. */
export function isLargeRemoval(remove: number, membersBefore: number): boolean {
  return (
    remove >= 25 || (membersBefore > 0 && remove * 10 >= membersBefore * 3)
  );
}

/** Bounded, idempotent contact-creation phase (migration 004): loop between
 *  preview-confirm and commit until remaining = 0. Each call fits the 8s
 *  role-level statement budget; contact creation inside commit would not. */
export async function prepareAllImportContacts(
  runId: string,
  onProgress?: (remaining: number) => void,
): Promise<void> {
  for (let i = 0; i < PREPARE_MAX_CALLS; i++) {
    // SMS plan §4.3: same bounded busy retry as commit (prepare inserts contacts and is idempotent).
    const { data, error } = await retryOnSmsNumberBusy(() =>
      supabase.rpc("prepare_segment_import_contacts", {
        p_run_id: runId,
        p_max: PREPARE_CONTACTS_BATCH,
      }),
    );
    if (error) {
      if (isSmsNumberBusy(error)) {
        throw new Error(SMS_NUMBER_BUSY_MESSAGE);
      }
      if (classifyImportErrorObject(error) !== "transport") {
        throw new Error(error.message);
      }
      await new Promise((r) => setTimeout(r, 1000));
      continue; // idempotent — safe to re-call
    }
    const res = data as { created: number; remaining: number };
    onProgress?.(res.remaining);
    if (res.remaining === 0) {
      return;
    }
  }
  throw new Error(
    "SEG_IMPORT_BOUNDS: contact preparation did not converge — contact support",
  );
}

export async function abortImportRun(runId: string): Promise<void> {
  const { error } = await supabase.rpc("abort_segment_import_run", {
    p_run_id: runId,
  });
  if (error) {
    throw new Error(error.message);
  }
}

export async function getImportRuns(
  listId: string,
  limit = 10,
): Promise<ImportRunSummary[]> {
  const { data, error } = await supabase
    .from("segment_import_runs")
    .select(
      "id, list_id, list_name, mode, status, filename, preview, result, created_by, created_at, committed_at",
    )
    .eq("list_id", listId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error(error.message);
  }
  return (data || []) as ImportRunSummary[];
}

/** Server-paginated removals audit (PostgREST caps unbounded selects at 1000). */
export async function getImportRunRemovals(
  runId: string,
  page: number,
  pageSize = 100,
): Promise<{
  rows: Array<{
    contact_id: string;
    email_at_removal: string | null;
    removed_at: string;
  }>;
  total: number;
}> {
  const from = page * pageSize;
  const { data, error, count } = await supabase
    .from("segment_import_run_removals")
    .select("contact_id, email_at_removal, removed_at", { count: "exact" })
    .eq("run_id", runId)
    .order("email_at_removal", { ascending: true, nullsFirst: false })
    .range(from, from + pageSize - 1);
  if (error) {
    throw new Error(error.message);
  }
  return { rows: data || [], total: count ?? 0 };
}
